import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auditAPI } from '../../api/api';
import { Printer, Sparkles, Loader2, FileText, RefreshCw } from 'lucide-react';

const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low'];

const AuditReport = ({ engagement }) => {
  const [reportData, setReportData] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const reportRef = useRef(null);

  const loadReportData = useCallback(async () => {
    if (!engagement?.id) return;
    setLoading(true);
    try {
      const res = await auditAPI.getReportData(engagement.id);
      setReportData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [engagement?.id]);

  useEffect(() => { loadReportData(); }, [loadReportData]);

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await auditAPI.generateSummary(engagement.id);
      setAiSummary(res.data);
    } catch (e) { console.error(e); alert('Failed to generate AI summary'); }
    setGeneratingSummary(false);
  };

  const handlePrint = () => {
    const content = reportRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Internal Audit Report - ${engagement.client_name}</title><style>${getPrintStyles()}</style></head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) return <div className="text-center py-16 text-gray-500"><Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />Loading report data...</div>;
  if (!reportData) return <div className="text-center py-16 text-gray-400"><FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />No report data available</div>;

  const d = reportData;
  const eng = d.engagement || {};
  const planning = d.planning || {};

  return (
    <div className="space-y-4" data-testid="audit-report-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white rounded-xl border p-3">
        <div className="flex items-center gap-2">
          <button onClick={handleGenerateSummary} disabled={generatingSummary}
            className="flex items-center gap-1.5 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            data-testid="generate-summary-btn">
            {generatingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generatingSummary ? 'Generating...' : 'Generate AI Summary'}
          </button>
          <button onClick={loadReportData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Refresh data">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
          data-testid="print-report-btn">
          <Printer className="w-4 h-4" /> Print Report
        </button>
      </div>

      {/* Report Preview */}
      <div className="bg-white rounded-xl border shadow-sm" data-testid="report-preview">
        <div ref={reportRef} className="p-8 max-w-[900px] mx-auto report-content">

          {/* ============ COVER PAGE ============ */}
          <div className="text-center mb-12 pb-8 border-b-2 border-gray-800" data-testid="report-cover">
            <p className="text-sm tracking-[0.3em] text-gray-500 uppercase mb-2">Confidential</p>
            <h1 className="text-3xl font-bold text-gray-900 mt-6 mb-2">Internal Audit Report</h1>
            <div className="w-20 h-1 bg-indigo-600 mx-auto my-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-1">{eng.client_name}</h2>
            <p className="text-base text-gray-600 mb-6">{eng.audit_type}</p>

            <div className="inline-block text-left bg-gray-50 rounded-lg px-8 py-4 mt-4">
              <table className="text-sm">
                <tbody>
                  <tr><td className="pr-6 py-1 text-gray-500 font-medium">Audit Period</td><td className="py-1 text-gray-800">{eng.period_from?.split('T')[0]} to {eng.period_to?.split('T')[0]}</td></tr>
                  <tr><td className="pr-6 py-1 text-gray-500 font-medium">Financial Year</td><td className="py-1 text-gray-800">{eng.financial_year}</td></tr>
                  <tr><td className="pr-6 py-1 text-gray-500 font-medium">Frequency</td><td className="py-1 text-gray-800">{eng.frequency}</td></tr>
                  {eng.branch_department && <tr><td className="pr-6 py-1 text-gray-500 font-medium">Branch/Dept</td><td className="py-1 text-gray-800">{eng.branch_department}</td></tr>}
                  <tr><td className="pr-6 py-1 text-gray-500 font-medium">Risk Category</td><td className="py-1 text-gray-800">{eng.risk_category}</td></tr>
                  {eng.engagement_partner_name && <tr><td className="pr-6 py-1 text-gray-500 font-medium">Engagement Partner</td><td className="py-1 text-gray-800">{eng.engagement_partner_name}</td></tr>}
                  {eng.audit_manager_name && <tr><td className="pr-6 py-1 text-gray-500 font-medium">Audit Manager</td><td className="py-1 text-gray-800">{eng.audit_manager_name}</td></tr>}
                  <tr><td className="pr-6 py-1 text-gray-500 font-medium">Report Date</td><td className="py-1 text-gray-800">{d.report_date}</td></tr>
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-400 mt-8">HYDER ALI AND ASSOCIATES</p>
            <p className="text-xs text-gray-400">Chartered Accountants</p>
          </div>

          {/* ============ TABLE OF CONTENTS ============ */}
          <div className="mb-10 page-break-before" data-testid="report-toc">
            <h2 className="text-lg font-bold text-gray-900 border-b-2 border-indigo-600 pb-2 mb-4">Table of Contents</h2>
            <div className="space-y-2 text-sm">
              {[
                { num: 1, title: 'Executive Summary' },
                { num: 2, title: 'Scope of Audit' },
                { num: 3, title: 'Audit Methodology' },
                { num: 4, title: 'Pending Observations from Previous Audit' },
                { num: 5, title: 'Observations of Current Period' },
                { num: 6, title: 'Risk Classification Summary' },
                { num: 7, title: 'Management Responses' },
                { num: 8, title: 'Recommendations' },
                { num: 9, title: 'Conclusion' },
              ].map(item => (
                <div key={item.num} className="flex items-center gap-2">
                  <span className="font-semibold text-indigo-600 w-8">{item.num}.</span>
                  <span className="flex-1 text-gray-700">{item.title}</span>
                  <span className="border-b border-dotted border-gray-300 flex-1"></span>
                </div>
              ))}
            </div>
          </div>

          {/* ============ SECTION 1: EXECUTIVE SUMMARY ============ */}
          <SectionHeader num={1} title="Executive Summary" />
          {aiSummary?.executive_summary ? (
            <p className="text-sm text-gray-700 leading-relaxed mb-8">{aiSummary.executive_summary}</p>
          ) : (
            <div className="bg-purple-50 rounded-lg p-4 mb-8 border border-purple-100">
              <p className="text-sm text-purple-700 italic">Click "Generate AI Summary" above to auto-generate the executive summary from audit data.</p>
              <div className="mt-3 text-xs text-gray-500 space-y-1">
                <p>Quick stats: {d.total_observations} observations total ({d.new_observations?.length || 0} new, {d.carried_forward_observations?.length || 0} carried forward)</p>
                <p>Overall compliance score: {d.overall_compliance_score}%</p>
              </div>
            </div>
          )}

          {/* ============ SECTION 2: SCOPE OF AUDIT ============ */}
          <SectionHeader num={2} title="Scope of Audit" />
          <div className="mb-8 space-y-3">
            {planning.objectives && (
              <div><h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Objectives</h4><p className="text-sm text-gray-700 leading-relaxed">{planning.objectives}</p></div>
            )}
            {planning.scope && (
              <div><h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Scope</h4><p className="text-sm text-gray-700 leading-relaxed">{planning.scope}</p></div>
            )}
            {planning.key_risk_areas && (
              <div><h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Key Risk Areas</h4><p className="text-sm text-gray-700 leading-relaxed">{planning.key_risk_areas}</p></div>
            )}
            {(planning.departments_covered || []).length > 0 && (
              <div><h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Departments Covered</h4><p className="text-sm text-gray-700">{planning.departments_covered.join(', ')}</p></div>
            )}
            {!planning.objectives && !planning.scope && (
              <p className="text-sm text-gray-400 italic">No planning data available. Go to Planning tab to add audit scope details.</p>
            )}
          </div>

          {/* ============ SECTION 3: AUDIT METHODOLOGY ============ */}
          <SectionHeader num={3} title="Audit Methodology" />
          <div className="mb-8">
            {aiSummary?.methodology ? (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">{aiSummary.methodology}</p>
            ) : (
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                The audit was conducted in accordance with the Standards on Internal Audit issued by the Institute of Chartered Accountants of India (ICAI).
                The methodology included review of documents and records, sample-based testing of transactions, evaluation of internal controls, and verification of compliance with applicable laws and regulations.
              </p>
            )}

            {/* Compliance Summary Table */}
            {d.checklist_summary?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Compliance Assessment Summary</h4>
                <table className="w-full text-sm border-collapse" data-testid="compliance-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Audit Area</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-16">Total</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-20">Complied</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-24">Not Complied</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-16">Partial</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-20">Score %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.checklist_summary.map((cs, i) => (
                      <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                        <td className="border border-gray-300 px-3 py-2 text-gray-800">{cs.area}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center">{cs.total}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-emerald-700 font-medium">{cs.complied}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-red-700 font-medium">{cs.not_complied}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center text-amber-700">{cs.partial}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center font-bold">{cs.score}%</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-50 font-semibold">
                      <td className="border border-gray-300 px-3 py-2 text-indigo-800">Overall Compliance Score</td>
                      <td colSpan={4} className="border border-gray-300"></td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-indigo-800 text-base">{d.overall_compliance_score}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* ============ SECTION 4: PENDING OBSERVATIONS (CARRIED FORWARD) ============ */}
          <SectionHeader num={4} title="Pending Observations from Previous Audit" />
          <div className="mb-8">
            {(d.carried_forward_observations || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No pending observations from previous audit periods.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-700 mb-3">The following {d.carried_forward_observations.length} observation(s) were carried forward from previous audit period(s) and remain pending:</p>
                {d.carried_forward_observations.map((obs, i) => (
                  <ObservationBlock key={obs.id} obs={obs} index={i} />
                ))}
              </div>
            )}
          </div>

          {/* ============ SECTION 5: CURRENT PERIOD OBSERVATIONS ============ */}
          <SectionHeader num={5} title="Observations of Current Period" />
          <div className="mb-8">
            {(d.new_observations || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No new observations recorded during the current audit period.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-700 mb-3">During the current audit period, {d.new_observations.length} observation(s) were recorded:</p>
                {d.new_observations.map((obs, i) => (
                  <ObservationBlock key={obs.id} obs={obs} index={i} />
                ))}
              </div>
            )}
          </div>

          {/* ============ SECTION 6: RISK CLASSIFICATION SUMMARY ============ */}
          <SectionHeader num={6} title="Risk Classification Summary" />
          <div className="mb-8">
            <table className="w-full text-sm border-collapse" data-testid="risk-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Risk Level</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-20">Count</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 w-24">% of Total</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Key Issues</th>
                </tr>
              </thead>
              <tbody>
                {RISK_ORDER.map(risk => {
                  const data = d.risk_summary?.[risk];
                  if (!data) return null;
                  const pct = d.total_observations > 0 ? Math.round((data.count / d.total_observations) * 100) : 0;
                  return (
                    <tr key={risk}>
                      <td className="border border-gray-300 px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${risk === 'Critical' ? 'bg-red-100 text-red-800' : risk === 'High' ? 'bg-orange-100 text-orange-800' : risk === 'Medium' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{risk}</span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center font-bold">{data.count}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">{pct}%</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs text-gray-600">{data.observations?.slice(0, 3).join('; ')}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="border border-gray-300 px-3 py-2">Total</td>
                  <td className="border border-gray-300 px-3 py-2 text-center">{d.total_observations}</td>
                  <td className="border border-gray-300 px-3 py-2 text-center">100%</td>
                  <td className="border border-gray-300"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ============ SECTION 7: MANAGEMENT RESPONSES ============ */}
          <SectionHeader num={7} title="Management Responses" />
          <div className="mb-8">
            {(d.management_responses || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No management responses recorded.</p>
            ) : (
              <table className="w-full text-sm border-collapse" data-testid="mgmt-response-table">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-16">Ref</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Observation</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Management Response</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-24">Status</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-28">Target Date</th>
                  </tr>
                </thead>
                <tbody>
                  {d.management_responses.map((mr, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-indigo-600">{mr.serial_no}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{mr.title}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{mr.response_text}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{mr.response_status}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{mr.target_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ============ SECTION 8: RECOMMENDATIONS ============ */}
          <SectionHeader num={8} title="Recommendations" />
          <div className="mb-8">
            {(d.recommendations || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic">No recommendations recorded.</p>
            ) : (
              <table className="w-full text-sm border-collapse" data-testid="recommendations-table">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-16">Ref</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-32">Area</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 w-16">Risk</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recommendations.map((rec, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-3 py-2 text-xs font-medium text-indigo-600">{rec.serial_no}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{rec.area}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${rec.risk_level === 'Critical' ? 'bg-red-100 text-red-700' : rec.risk_level === 'High' ? 'bg-orange-100 text-orange-700' : rec.risk_level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{rec.risk_level}</span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{rec.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ============ SECTION 9: CONCLUSION ============ */}
          <SectionHeader num={9} title="Conclusion" />
          <div className="mb-8">
            {aiSummary?.conclusion ? (
              <p className="text-sm text-gray-700 leading-relaxed">{aiSummary.conclusion}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">Click "Generate AI Summary" to auto-generate the conclusion.</p>
            )}
          </div>

          {/* ============ SIGN-OFF ============ */}
          <div className="mt-16 pt-8 border-t-2 border-gray-300" data-testid="report-signoff">
            <div className="grid grid-cols-3 gap-8 text-sm">
              <div className="text-center">
                <div className="border-b border-gray-400 pb-1 mb-2 h-12"></div>
                <p className="font-semibold text-gray-700">Prepared By</p>
                <p className="text-xs text-gray-500 mt-1">Audit Staff</p>
              </div>
              <div className="text-center">
                <div className="border-b border-gray-400 pb-1 mb-2 h-12"></div>
                <p className="font-semibold text-gray-700">Reviewed By</p>
                <p className="text-xs text-gray-500 mt-1">{eng.audit_manager_name || 'Audit Manager'}</p>
              </div>
              <div className="text-center">
                <div className="border-b border-gray-400 pb-1 mb-2 h-12"></div>
                <p className="font-semibold text-gray-700">Approved By</p>
                <p className="text-xs text-gray-500 mt-1">{eng.engagement_partner_name || 'Engagement Partner'}</p>
              </div>
            </div>
            <div className="text-center mt-10">
              <p className="text-sm font-semibold text-gray-700">HYDER ALI AND ASSOCIATES</p>
              <p className="text-xs text-gray-500">Chartered Accountants</p>
              <p className="text-xs text-gray-400 mt-1">Date: {d.report_date}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Section Header component for consistent styling
const SectionHeader = ({ num, title }) => (
  <div className="mb-4 mt-8 first:mt-0 page-break-before-avoid">
    <h2 className="text-base font-bold text-gray-900 border-b-2 border-indigo-600 pb-2 flex items-center gap-2">
      <span className="bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{num}</span>
      {title}
    </h2>
  </div>
);

// Observation block for report sections
const ObservationBlock = ({ obs, index }) => {
  const gen = obs.generated || {};
  const hasGenerated = gen.title || gen.observation;
  const cf = obs.carry_forward || {};

  return (
    <div className="border border-gray-200 rounded-lg p-4 page-break-inside-avoid" data-testid={`report-obs-${obs.id}`}>
      <div className="flex items-start gap-3 mb-2">
        <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded flex-shrink-0">{obs.serial_no}</span>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900">{gen.title || obs.hint?.substring(0, 80) || 'Untitled'}</h4>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
            <span className={`px-1.5 py-0.5 rounded font-medium ${obs.risk_level === 'Critical' ? 'bg-red-100 text-red-700' : obs.risk_level === 'High' ? 'bg-orange-100 text-orange-700' : obs.risk_level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{obs.risk_level}</span>
            <span>{obs.audit_area}</span>
            {obs.amount_involved > 0 && <span>Amount: Rs. {obs.amount_involved?.toLocaleString('en-IN')}</span>}
            {cf.is_carried_forward && <span className="text-amber-600 font-medium">Carried Forward (#{cf.carry_forward_count}) from {cf.original_audit_period}</span>}
          </div>
        </div>
      </div>

      {hasGenerated ? (
        <div className="space-y-2 ml-8">
          <div><h5 className="text-xs font-semibold text-gray-500 uppercase">Observation</h5><p className="text-sm text-gray-700 leading-relaxed">{gen.observation}</p></div>
          {gen.risk_impact && <div><h5 className="text-xs font-semibold text-gray-500 uppercase">Risk / Impact</h5><p className="text-sm text-gray-700">{gen.risk_impact}</p></div>}
          {gen.recommendation && <div><h5 className="text-xs font-semibold text-gray-500 uppercase">Recommendation</h5><p className="text-sm text-gray-700">{gen.recommendation}</p></div>}
        </div>
      ) : (
        <div className="ml-8"><h5 className="text-xs font-semibold text-gray-500 uppercase">Staff Notes</h5><p className="text-sm text-gray-700">{obs.hint}</p></div>
      )}

      {/* Management Response inline */}
      {obs.management_response?.response_text && (
        <div className="ml-8 mt-3 bg-blue-50 rounded p-3 border border-blue-100">
          <h5 className="text-xs font-semibold text-blue-600 uppercase">Management Response</h5>
          <p className="text-xs text-gray-700 mt-1">{obs.management_response.response_text}</p>
          {obs.management_response.corrective_action && <p className="text-xs text-gray-500 mt-1">Corrective Action: {obs.management_response.corrective_action}</p>}
        </div>
      )}

      {/* Auditor Rejoinder inline */}
      {obs.auditor_rejoinder?.comment && (
        <div className="ml-8 mt-2 bg-purple-50 rounded p-3 border border-purple-100">
          <h5 className="text-xs font-semibold text-purple-600 uppercase">Auditor's Rejoinder</h5>
          <p className="text-xs text-gray-700 mt-1">{obs.auditor_rejoinder.comment}</p>
          <p className="text-xs text-gray-500 mt-1">Status: {obs.auditor_rejoinder.final_status}</p>
        </div>
      )}
    </div>
  );
};

// Print-specific CSS
function getPrintStyles() {
  return `
    @page { size: A4; margin: 20mm 15mm; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1a1a1a; line-height: 1.6; font-size: 11pt; }
    h1 { font-size: 22pt; font-weight: 700; color: #111; margin-bottom: 8px; }
    h2 { font-size: 13pt; font-weight: 700; color: #111; border-bottom: 2px solid #4f46e5; padding-bottom: 6px; margin-top: 24px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
    h3 { font-size: 11pt; font-weight: 600; color: #333; }
    h4 { font-size: 9pt; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    h5 { font-size: 8pt; font-weight: 600; color: #666; text-transform: uppercase; margin-bottom: 2px; }
    p { font-size: 10pt; color: #333; margin-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; font-size: 9pt; }
    th { background: #f3f4f6; font-weight: 600; text-align: left; padding: 6px 8px; border: 1px solid #d1d5db; }
    td { padding: 5px 8px; border: 1px solid #d1d5db; vertical-align: top; }
    tr:nth-child(even) { background: #f9fafb; }
    tfoot tr { background: #eef2ff !important; font-weight: 600; }
    .page-break-before { page-break-before: always; }
    .page-break-inside-avoid { page-break-inside: avoid; }
    .border-b-2 { border-bottom: 2px solid; }
    .border-indigo-600 { border-color: #4f46e5; }
    .bg-indigo-600 { background-color: #4f46e5; }
    .text-indigo-600 { color: #4f46e5; }
    .bg-indigo-50 { background-color: #eef2ff; }
    .bg-gray-50 { background-color: #f9fafb; }
    .bg-gray-100 { background-color: #f3f4f6; }
    .bg-blue-50 { background-color: #eff6ff; }
    .bg-purple-50 { background-color: #faf5ff; }
    .bg-red-100 { background-color: #fee2e2; }
    .bg-orange-100 { background-color: #ffedd5; }
    .bg-amber-100 { background-color: #fef3c7; }
    .bg-emerald-100 { background-color: #d1fae5; }
    .text-red-700, .text-red-800 { color: #b91c1c; }
    .text-orange-700, .text-orange-800 { color: #c2410c; }
    .text-amber-700, .text-amber-800 { color: #b45309; }
    .text-emerald-700, .text-emerald-800 { color: #047857; }
    .text-blue-600 { color: #2563eb; }
    .text-purple-600 { color: #9333ea; }
    .rounded { border-radius: 4px; }
    .rounded-lg { border-radius: 8px; }
    .rounded-full { border-radius: 9999px; }
    .px-1\\.5 { padding-left: 6px; padding-right: 6px; }
    .px-2 { padding-left: 8px; padding-right: 8px; }
    .py-0\\.5 { padding-top: 2px; padding-bottom: 2px; }
    .py-1 { padding-top: 4px; padding-bottom: 4px; }
    .p-3 { padding: 12px; }
    .p-4 { padding: 16px; }
    .ml-8 { margin-left: 32px; }
    .mt-1 { margin-top: 4px; }
    .mt-2 { margin-top: 8px; }
    .mt-3 { margin-top: 12px; }
    .mb-2 { margin-bottom: 8px; }
    .mb-3 { margin-bottom: 12px; }
    .space-y-2 > * + * { margin-top: 8px; }
    .space-y-4 > * + * { margin-top: 16px; }
    .flex { display: flex; }
    .items-center { align-items: center; }
    .items-start { align-items: flex-start; }
    .gap-2 { gap: 8px; }
    .gap-3 { gap: 12px; }
    .gap-8 { gap: 32px; }
    .flex-1 { flex: 1 1 0%; }
    .flex-shrink-0 { flex-shrink: 0; }
    .grid { display: grid; }
    .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .w-7 { width: 28px; }
    .h-7 { height: 28px; }
    .h-12 { height: 48px; }
    .w-20 { width: 80px; }
    .text-center { text-align: center; }
    .text-xs { font-size: 8pt; }
    .text-sm { font-size: 10pt; }
    .text-base { font-size: 11pt; }
    .text-lg { font-size: 13pt; }
    .text-xl { font-size: 16pt; }
    .text-3xl { font-size: 22pt; }
    .font-medium { font-weight: 500; }
    .font-semibold { font-weight: 600; }
    .font-bold { font-weight: 700; }
    .uppercase { text-transform: uppercase; }
    .italic { font-style: italic; }
    .leading-relaxed { line-height: 1.7; }
    .border { border: 1px solid #e5e7eb; }
    .border-t-2 { border-top: 2px solid; }
    .border-b { border-bottom: 1px solid; }
    .border-gray-200 { border-color: #e5e7eb; }
    .border-gray-300 { border-color: #d1d5db; }
    .border-gray-400 { border-color: #9ca3af; }
    .border-blue-100 { border-color: #dbeafe; }
    .border-purple-100 { border-color: #f3e8ff; }
    .inline-block { display: inline-block; }
    @media print {
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
}

export default AuditReport;
